import { useNavigate } from '@tanstack/react-router'
import type { NavBadgeValue } from '@workspace/seedforge-ui/components/app-sidebar'
import {
  InvestorLayout as BaseInvestorLayout,
  type InvestorLayoutProps,
} from '@workspace/seedforge-ui/components/investor-layout'
import type { UserMenuLink } from '@workspace/seedforge-ui/components/user-menu'
import { INVESTOR_NAV_LABEL } from '@workspace/seedforge-ui/lib/investor-nav'
import { Shield } from 'lucide-react'
import { useMemo } from 'react'
import { useInvestorContext } from '../contexts/investor-guard'
import { copy } from '../features/investor-assistant/copy/investor-copy'
import {
  useMcpSettingsQuery,
  useNewWatchdogMatches,
  useUnreadUpdates,
} from '../features/investor-assistant/hooks/use-investor-assistant'
import { useBasePath } from '../hooks/use-base-path'
import { useSignOut } from '../hooks/use-sign-out'
import { useFeedback } from '../routes/app'

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/** An orange count for news the investor has not seen; nothing when there is none. */
function newsBadge(n: number): NavBadgeValue | undefined {
  return n > 0
    ? { text: String(n), tone: 'primary', title: copy('watchdog.new', { n }) }
    : undefined
}

export function InvestorLayout(
  props: Omit<
    InvestorLayoutProps,
    | 'onSignOut'
    | 'onSendFeedback'
    | 'userName'
    | 'userEmail'
    | 'basePath'
    | 'activeInvestor'
    | 'investors'
    | 'onSwitchInvestor'
    | 'logoSrc'
    | 'creditBalance'
    | 'variant'
  >
) {
  const { user, investor, investors, switchInvestor, creditBalance } = useInvestorContext()
  const signOut = useSignOut()
  const openFeedback = useFeedback()
  const basePath = useBasePath()
  const navigate = useNavigate()
  const { data: assistant, isPending } = useMcpSettingsQuery()
  const assistantEnabled = assistant?.data.assistantEnabled ?? false
  // The watchdog is part of the assistant app, so its endpoint is only asked once the app is open.
  const newMatches = useNewWatchdogMatches(assistantEnabled)
  const unreadUpdates = useUnreadUpdates(assistantEnabled)
  const extraLinks = useMemo<UserMenuLink[]>(() => {
    if (user.role !== 'ADMIN') return []
    return [{ icon: Shield, label: 'Admin', to: '/admin' }]
  }, [user.role])

  const investorItems = investors.map((i) => ({
    id: i.id,
    name: i.name,
    initials: getInitials(i.name),
    role: i.role,
  }))

  const activeItem = {
    id: investor.id,
    name: investor.name,
    initials: getInitials(investor.name),
  }
  // A solo investor whose fund is their own name would show the same initials twice: the user menu
  // already carries them, so the switcher is left out.
  const onlyThemselves = investors.length === 1 && investor.name === user.name

  return (
    <BaseInvestorLayout
      {...props}
      basePath={basePath}
      logoSrc="/seedforge-icon.svg"
      userName={user.name ?? undefined}
      userEmail={user.email}
      onSignOut={signOut}
      onSendFeedback={openFeedback}
      activeInvestor={onlyThemselves ? undefined : activeItem}
      investors={investorItems}
      onSwitchInvestor={switchInvestor}
      creditBalance={creditBalance}
      onInvestorNameClick={() => navigate({ to: basePath })}
      userMenuExtraLinks={extraLinks}
      variant={isPending ? 'pending' : assistantEnabled ? 'assistant' : 'classic'}
      navBadges={{
        [INVESTOR_NAV_LABEL.search]: newsBadge(newMatches),
        [INVESTOR_NAV_LABEL.following]: newsBadge(unreadUpdates),
      }}
    />
  )
}
